import classNames from 'classnames';
import Clipboard from 'clipboard';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InfoCircleIcon } from 'tdesign-icons-react';
import { Button, Loading, MessagePlugin, Popconfirm, Progress, Tooltip } from 'tdesign-react';

import { CheckCircle, Copy3, EditorDel, Reload } from '@/components/icon';
import { AiMusicReq, ResourceHistory, Status, UrlInfo } from '@/interface/aigc';
import AudioEditor from '@/material/form-upload/audio/AudioEditor';
import { deleteAIResource } from '@/services/aigc';
import { CgiErrorCode } from '@/services/cgi';

import styles from './index.module.less';

export interface HistoryItemProps {
  history: ResourceHistory;
  selectedMusic: string;
  generateMusic: (postData: AiMusicReq) => void;
  handleSelect: (url: UrlInfo) => void;
  onDelete: (id: string) => void;
}

const MusicHistoryItem: React.FC<HistoryItemProps> = ({
  history,
  selectedMusic,
  generateMusic,
  handleSelect,
  onDelete,
}) => {
  const { status: historyStatus, urls, prompt, relateId, aiTxt2ImgReq = '' } = history;
  const loading = (historyStatus === Status.WAITING
    && !urls.some(e => (e.url && e.url !== 'initUrl') || e.errMsg));

  const [progress, setProgress] = useState<number>(0);
  const isValid = (url: string) => url && url !== 'initUrl';
  const isSelected = (url: string) => isValid(url) && url === selectedMusic;

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      setProgress(0);
      interval = setInterval(() => {
        setProgress((prev) => {
          const random = Math.random();
          if (prev < 40) {
            return Math.min(prev + 8 + Math.floor(random * 8), 40);
          } if (prev < 70) {
            return Math.min(prev + 6 + Math.floor(random * 6), 70);
          } if (prev < 95) {
            return Math.min(prev + 5 + Math.floor(random * 5), 95);
          } if (prev < 99) {
            return Math.min(prev + 1 + Math.floor(random * 3), 99);
          }
          return prev;
        });
      }, 2000);
    } else {
      setProgress(100);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const aiMusicReq: AiMusicReq = useMemo(() => {
    try {
      return JSON.parse(aiTxt2ImgReq);
    } catch {
      return {
        prompt: '',
      };
    }
  }, [aiTxt2ImgReq]);

  const handleRegenerate = () => {
    const postData: AiMusicReq = {
      ...aiMusicReq,
      prompt,
      queryId: '',
    };
    generateMusic(postData);
  };

  const handleDelete = async () => {
    const res = await deleteAIResource({ relateIds: [relateId] });
    if (res.code !== CgiErrorCode.NO_ERROR || !res.data.hasDeleted) {
      MessagePlugin.error('删除失败，请稍后重试');
      return;
    }
    MessagePlugin.success('删除成功');
    onDelete(relateId);
  };

  const clickMusic = (urlInfo: UrlInfo) => {
    const { url } = urlInfo;
    if (isValid(url)) {
      handleSelect(urlInfo);
    }
  };

  const onVolumeChange = (volume: number, urlInfo: UrlInfo) => {
    handleSelect({ ...urlInfo, volume });
  };

  const copyBtnRef = useRef(null);

  useEffect(() => {
    if (copyBtnRef.current) {
      const clipboard = new Clipboard(copyBtnRef.current, {
        text: () => prompt,
      });

      clipboard.on('success', () => {
        MessagePlugin.success('已复制到剪贴板');
      });

      clipboard.on('error', () => {
        MessagePlugin.error('复制失败');
      });

      return () => {
        clipboard.destroy();
      };
    }
  }, [prompt]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.prompt}>{prompt}</div>
        <div className={styles.buttons}>
            <Button
              theme="default"
              className={styles.btn}
              onClick={handleRegenerate}
            >
              <div className={styles.text}>
                <Reload />重新生成
              </div>
            </Button>
            <Button
              theme="default"
              className={styles.btn}
              ref={copyBtnRef}
            >
              <div className={styles.text}>
                <Copy3 />复制描述
              </div>
            </Button>
          <Popconfirm
            content={'删除生成记录'}
            onConfirm={handleDelete}
          >
            <Button
              theme="default"
              className={styles.delBtn}
              disabled={historyStatus !== Status.FINISHED}
            >
              <div className={styles.text}>
                <EditorDel />
              </div>
            </Button>
          </Popconfirm>
          </div>
      </div>
      <div className={styles.content}>
        <Loading
          showOverlay
          loading={loading}
          text="生成中，您也可以离开该页面稍后再来查看"
          style={{ display: 'flex', width: '100%', height: '100%', gap: 10 }}
          indicator={
            <Progress
              percentage={progress}
              theme={'circle'}
              size={60}
            />}
        >
          {urls.map((urlInfo, index) => {
            const { url, name, errMsg: err, volume = 100 } = urlInfo;
            return (
              <Loading
                key={index}
                showOverlay
                loading={!url && !err && !loading && historyStatus !== Status.FINISHED}
                style={{ display: 'flex', flex: 1, height: '100%' }}
              >
                <div
                  key={index}
                  className={classNames(styles.music, { [styles.selected]: isSelected(url) })}
                  onClick={() => clickMusic(urlInfo)}
                >
                  {isValid(url)
                    ? <AudioEditor
                        url={url}
                        name={name}
                        height={64}
                        isEdit={false}
                      volume={volume}
                      onVolumeChange={v => onVolumeChange(v, urlInfo)}
                      />
                    : ((historyStatus === Status.FINISHED || err)
                      && <Tooltip content={err}>
                        <div className={styles.fail}>
                          <InfoCircleIcon />
                          <div>生成失败</div>
                        </div>
                      </Tooltip>)}
                  {isSelected(url) && <CheckCircle className={styles.icon} />}
                </div>
              </Loading>
            );
          })}
        </Loading>
      </div>
    </div>
  );
};

export default MusicHistoryItem;
